import React from "react";
import { Box, IconButton, Typography, Avatar, Tooltip } from "@mui/material";
import LanguageToggle from '../core/LanguageToggle';
import { styled } from "@mui/material/styles";
import { getAvatarById } from "../../../utils/avatar.utils";
import avatarDefault from "../../../assets/images/avatars/avatar_default.png";

const UserSectionBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: "1px solid rgba(255,255,255,0.2)",
}));

const LanguageToggleBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  justifyContent: expanded ? "flex-start" : "center",
}));

const UserInfoBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: expanded ? "flex-start" : "center",
}));

const UserAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  height: expanded ? 48 : 40,
  width: expanded ? 48 : 40,
  cursor: "pointer",
  border: "2px solid rgba(255,255,255,0.3)",
  transition: "border 0.2s",
  "&:hover": {
    border: "2px solid rgba(255,255,255,0.6)",
  },
}));

const UserInfoTextBox = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(1.5),
  flexGrow: 1,
}));

const UserNameTypography = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  lineHeight: 1.2,
}));

const UserRoleTypography = styled(Typography)(({ theme }) => ({
  opacity: 0.8,
  lineHeight: 1.2,
}));

interface UserSectionProps {
  expanded: boolean;
  userName: string;
  userRole: string;
  avatarId: number | null;
  userType: "admin" | "principal" | "student";
  onAvatarClick: (event: React.MouseEvent<HTMLElement>) => void;
  onLanguageToggle: () => void;
  isRtl: boolean;
  t: (key: string) => string;
}

const UserSection: React.FC<UserSectionProps> = ({
  expanded,
  userName,
  userRole,
  avatarId,
  userType,
  onAvatarClick,
  onLanguageToggle,
  isRtl,
  t,
}) => {
  const getAvatarSrc = () => {
    if (!avatarId) {
      return avatarDefault;
    }
    const avatarSrc = getAvatarById(avatarId, userType);
    return typeof avatarSrc === "string" && avatarSrc.length > 0
      ? avatarSrc
      : avatarDefault;
  };

  return (
    <UserSectionBox>
      {/* Language Toggle */}
      <LanguageToggleBox expanded={expanded}>
        <LanguageToggle />
      </LanguageToggleBox>

      {/* User Avatar and Info */}
      <UserInfoBox expanded={expanded}>
        <Tooltip title={userName || ""} placement={isRtl ? "left" : "right"}>
          <IconButton onClick={onAvatarClick} sx={{ p: 0 }}>
            <UserAvatar src={getAvatarSrc()} alt="תמונת פרופיל" expanded={expanded} />
          </IconButton>
        </Tooltip>

        {expanded && (
          <UserInfoTextBox>
            <UserNameTypography variant="body2">
              {userName || ""}
            </UserNameTypography>
            <UserRoleTypography variant="caption">
              {userRole}
            </UserRoleTypography>
          </UserInfoTextBox>
        )}
      </UserInfoBox>
    </UserSectionBox>
  );
};

export default UserSection;